import type { ComponentType } from "react";
import { PureComponent } from "react";

import { type IComponent, defaultAttributes } from "./Component";
import { ErrorContainer } from "./ErrorContainer";
import { ErrorMessage, type IErrorMessage } from "./ErrorMessage";
import { type ILoading, Loading } from "./Loading";
import { AjaxMethod } from "./Modules/Ajax";
import { Ajax, type AjaxFail, type AjaxSuccess, type IAjax } from "./Modules/Ajax";
import {
    ErrorType,
    type IErrorComponent,
    type IErrors,
    type RenderErrorCallback,
} from "./Modules/ErrorHandler";
import { clearAllErrors, displayErrorMessage, validateComponent } from "./Modules/ErrorHandler";
import { defaultToTrue } from "./Modules/Helpers";

interface IComponentAjaxInternal extends IComponent, IAjax {
    loading?: ILoading;
    hideLoading?: boolean;
    ajaxIgnoreError?: boolean;
    initialData?: any;
    ajaxSuccess?: any;
    ajaxFail?: any;
}

export interface IComponentAjax<TResult extends object | string | boolean | number | void>
    extends IComponentAjaxInternal {
    initialData?: TResult | undefined;
    ajaxSuccess?: AjaxSuccess<TResult> | Array<AjaxSuccess<TResult>>;
    ajaxFail?: AjaxFail | AjaxFail[];
}

export interface IStaticComponent {
    type: ComponentType;
    props: IErrorMessage;
}

export interface IComponentAjaxState {
    isLoading: boolean;
    renderErrorContainer?: boolean;
}

export abstract class ComponentAjax<
        TResult extends object | string | boolean | number | void,
        TProps extends IComponentAjax<TResult>,
        TState extends IComponentAjaxState = { isLoading: false },
    >
    extends PureComponent<TProps, TState>
    implements IErrorComponent
{
    public static defaultProps: IComponentAjaxInternal = {
        ajaxMethod: AjaxMethod.Get,
        ajaxIgnoreError: false,
        ajaxSuccess: [],
        ajaxFail: [],
        initialData: null,
        hideLoading: false,
    };

    private static isErrorResponse: <TResponse>(response: TResponse | IErrorMessage) => boolean = (
        response,
    ) => {
        const errorMessage = response as IErrorMessage;
        if (errorMessage?.errorName !== null && errorMessage.errorName !== undefined) {
            return true;
        }

        return false;
    };

    public errors: IErrors = this.defaultErrors();

    protected ajax: Ajax<TResult> | null = null;

    private xhrRequest: XMLHttpRequest | undefined;

    constructor(props: TProps) {
        super(props);
        this.initializeAjax(props);
    }

    public componentWillUnmount() {
        this.abortAjaxRequest();
    }

    private success = (
        response: TResult | IErrorMessage | IErrorMessage[],
        xhr: XMLHttpRequest,
    ): void => {
        if (!this.props.ajaxIgnoreError) {
            this.displayAjaxErrors(response as IErrorMessage);
        }

        if (this.ajaxSuccess) {
            this.ajaxSuccess(response as TResult, xhr);
        }

        if (this.props.ajaxSuccess) {
            if (Array.isArray(this.props.ajaxSuccess)) {
                for (let i = 0; i < this.props.ajaxSuccess.length; i += 1) {
                    this.props.ajaxSuccess[i].call(this, response, xhr);
                }
            } else {
                const fn = this.props.ajaxSuccess as AjaxSuccess<TResult>;
                fn.call(this, response, xhr);
            }
        }
    };

    private displayAjaxErrors = (
        errorMessage: IErrorMessage | IErrorMessage[],
        renderCallback?: RenderErrorCallback,
    ) => {
        if (errorMessage == null || errorMessage === undefined) {
            return;
        }

        const displayError = (em: IErrorMessage) => {
            if (ComponentAjax.isErrorResponse(em) === false) {
                return;
            }

            displayErrorMessage(this, em, renderCallback);
        };

        if (errorMessage instanceof Array) {
            for (let i = 0; i < errorMessage.length; i += 1) {
                displayError(errorMessage[i]);
            }
        } else {
            displayError(errorMessage);
        }
    };

    private failCallbacks = (
        error: TResult | string | IErrorMessage,
        xhr?: XMLHttpRequest,
    ): boolean => {
        let showError: boolean = true;

        if (this.ajaxFail) {
            showError = this.ajaxFail(error as IErrorMessage, xhr);
        }

        if (this.props.ajaxFail) {
            if (Array.isArray(this.props.ajaxFail)) {
                for (let i = 0; i < this.props.ajaxFail.length; i += 1) {
                    showError =
                        this.props.ajaxFail[i].call(this, error, xhr) !== false && showError;
                }
            } else {
                const fn = this.props.ajaxFail as AjaxFail;
                showError = fn.call(this, error, xhr) !== false && showError;
            }
        }

        return showError;
    };

    private fail = (error: TResult | string | IErrorMessage, xhr: XMLHttpRequest): boolean => {
        let showError: boolean = this.failCallbacks(error, xhr);

        showError = !this.props.ajaxIgnoreError && showError;

        if (!showError) {
            return false;
        }

        this.setState({ renderErrorContainer: true }, () => this.showError(error, xhr));

        return true;
    };

    private initializeAjax = (props: TProps) => {
        this.ajax = new Ajax(props);
        this.ajax.success = this.success;
        this.ajax.fail = this.fail;
    };

    private showError = (error: TResult | string | IErrorMessage, xhr: XMLHttpRequest) => {
        if (error === "timeout") {
            this.displayAjaxErrors({ message: "Timeout" } as IErrorMessage);
        } else if (xhr.status === 401 || xhr.status === 403) {
            const renderUnauthorized = () => ErrorMessage;

            this.displayAjaxErrors(error as IErrorMessage, renderUnauthorized);
        } else if (ComponentAjax.isErrorResponse(error as IErrorMessage) === true) {
            this.displayAjaxErrors(error as IErrorMessage);
        } else {
            this.displayAjaxErrors({
                errorName: "default",
                message: "Unknown error",
            } as IErrorMessage);
        }
    };

    protected rootDefaultAttributes(...className: string[]) {
        return defaultAttributes(this.props, ...className);
    }

    protected abstract ajaxFail(error: string | IErrorMessage, xhr?: XMLHttpRequest): boolean;

    protected abstract ajaxSuccess(response: TResult, xhr?: XMLHttpRequest): void;

    protected abortAjaxRequest() {
        if (this.xhrRequest) {
            this.xhrRequest.abort();
            this.xhrRequest = undefined;
        }
    }

    protected ajaxRequest(
        params?: object,
        validationSuccess?: () => void,
        validationFailed?: (errors: IErrorMessage[]) => void,
    ): void {
        if (!this.ajax) {
            return;
        }

        this.abortAjaxRequest();

        clearAllErrors(this);

        const isValid = validateComponent(this, validationSuccess, validationFailed);

        if (!isValid) {
            return;
        }

        let urlParams = {};
        if (!params && this.ajax.ajaxMethod === AjaxMethod.Get) {
            urlParams = this.props;
        } else if (this.ajax.ajaxMethod === AjaxMethod.Get) {
            const o = this.props as object;
            urlParams = { ...o, ...params };
        }

        this.xhrRequest = this.ajax.request(urlParams, params);
    }

    protected defaultErrors(): IErrors {
        return {
            default: {
                name: "default",
                type: ErrorType.Error,
                container: { ref: "default-error-container" },
                onValidate: defaultToTrue,
            },
        };
    }

    protected renderLoading() {
        return (
            this.state.isLoading &&
            !this.props.hideLoading && <Loading {...this.props.loading} isModal />
        );
    }

    protected renderAjaxErrorContainer(): JSX.Element | null {
        if (!this.state.renderErrorContainer) {
            return null;
        }

        if (!this.errors || !this.errors.default || !this.errors.default.container) {
            // eslint-disable-next-line no-console
            console.error("No default error found in component's errors object");
            return null;
        }

        return <ErrorContainer {...this.errors.default.container} />;
    }
}
